"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { cicToType } from "@/helpers/Helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import { ApiDrop } from "@/generated/models/ApiDrop";
import WaveDropTime from "./time/WaveDropTime";
import UserProfileTooltipWrapper from "@/components/utils/tooltip/UserProfileTooltipWrapper";
import { ArtistSubmissionBadge } from "./ArtistSubmissionBadge";
import { ArtistPreviewModal } from "./ArtistPreviewModal";
import { ProfileWinnerBadge } from "./ProfileWinnerBadge";
import { useState, useCallback, useMemo } from "react";
interface WaveDropHeaderProps {
  readonly drop: ApiDrop;
  readonly isStorm: boolean;
  readonly currentPartIndex: number;
  readonly partsCount: number;
  readonly showWaveInfo: boolean;
  readonly badge?: React.ReactNode;
}
const WaveDropHeader: React.FC<WaveDropHeaderProps> = t0 => {
  const $ = _c(64);
  const {
    drop,
    isStorm,
    currentPartIndex,
    partsCount,
    showWaveInfo,
    badge
  } = t0;
  const router = useRouter();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isWinnerModalOpen, setIsWinnerModalOpen] = useState(false);
  let t1;
  if ($[0] !== drop.author.cic) {
    t1 = cicToType(drop.author.cic);
    $[0] = drop.author.cic;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const cicType = t1;
  const submissionCount = drop.author.active_main_stage_submission_ids?.length || 0;
  const hasSubmissions = submissionCount > 0;
  const isWinner = drop.author.winner_main_stage_drop_ids && drop.author.winner_main_stage_drop_ids.length > 0;
  let t2;
  if ($[2] !== router) {
    t2 = (e, path) => {
      e.preventDefault();
      e.stopPropagation();
      router.push(path);
    };
    $[2] = router;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleNavigation = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setIsModalOpen(true);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleSubmissionBadgeClick = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsModalOpen(false);
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleModalClose = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsWinnerModalOpen(true);
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleWinnerBadgeClick = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsWinnerModalOpen(false);
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleWinnerModalClose = t6;
  let t7;
  if ($[8] !== cicType || $[9] !== drop.author.level) {
    t7 = <UserCICAndLevel level={drop.author.level} cicType={cicType} size={UserCICAndLevelSize.SMALL} />;
    $[8] = cicType;
    $[9] = drop.author.level;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = drop.author.handle ?? drop.author.id;
  let t9;
  if ($[11] !== drop.author.handle || $[12] !== handleNavigation) {
    t9 = e_0 => handleNavigation(e_0, `/${drop.author.handle}`);
    $[11] = drop.author.handle;
    $[12] = handleNavigation;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = `/${drop.author.handle}`;
  let t11;
  if ($[14] !== drop.author.handle || $[15] !== t10 || $[16] !== t9) {
    t11 = <Link onClick={t9} href={t10} className="tw-no-underline desktop-hover:hover:tw-underline tw-text-iron-200 desktop-hover:hover:tw-text-opacity-80 tw-transition tw-duration-300 tw-ease-out">{drop.author.handle}</Link>;
    $[14] = drop.author.handle;
    $[15] = t10;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t8) {
    t12 = <p className="tw-text-md tw-mb-0 tw-leading-none tw-font-semibold"><UserProfileTooltipWrapper user={t8}>{t11}</UserProfileTooltipWrapper></p>;
    $[18] = t11;
    $[19] = t8;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== drop.id || $[22] !== hasSubmissions || $[23] !== submissionCount) {
    t13 = hasSubmissions && <ArtistSubmissionBadge submissionCount={submissionCount} onBadgeClick={handleSubmissionBadgeClick} tooltipId={`header-badge-${drop.id}`} />;
    $[21] = drop.id;
    $[22] = hasSubmissions;
    $[23] = submissionCount;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== drop.id || $[26] !== isWinner) {
    t14 = isWinner && <ProfileWinnerBadge winCount={1} onBadgeClick={handleWinnerBadgeClick} tooltipId={`winner-badge-${drop.id}`} />;
    $[25] = drop.id;
    $[26] = isWinner;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="tw-size-[3px] tw-bg-iron-600 tw-rounded-full tw-flex-shrink-0" />;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== drop.created_at) {
    t16 = <WaveDropTime timestamp={drop.created_at} />;
    $[29] = drop.created_at;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t12 || $[32] !== t13 || $[33] !== t14 || $[34] !== t16 || $[35] !== t7) {
    t17 = <div className="tw-flex tw-items-center tw-gap-x-2">{t7}{t12}{t13}{t14}{t15}{t16}</div>;
    $[31] = t12;
    $[32] = t13;
    $[33] = t14;
    $[34] = t16;
    $[35] = t7;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== badge) {
    t18 = badge && <div className="tw-ml-2">{badge}</div>;
    $[37] = badge;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t17 || $[40] !== t18) {
    t19 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-2"><div className="tw-flex tw-items-center tw-gap-x-2">{t17}{t18}</div></div>;
    $[39] = t17;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== drop.wave || $[43] !== handleNavigation || $[44] !== showWaveInfo) {
    t20 = showWaveInfo && drop.wave && (() => {
      const waveMeta = (drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean;
            };
          };
        };
      })?.chat;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage: waveMeta?.scope?.group?.is_direct_message ?? false,
        isApp: false
      });
      return <Link onClick={e_1 => handleNavigation(e_1, waveHref)} href={waveHref} className="tw-mb-0 tw-text-[11px] tw-leading-0 -tw-mt-1 tw-text-iron-500 hover:tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out tw-no-underline">{drop.wave.name}</Link>;
    })();
    $[42] = drop.wave;
    $[43] = handleNavigation;
    $[44] = showWaveInfo;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t20) {
    t21 = <div>{t20}</div>;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== currentPartIndex || $[49] !== isStorm || $[50] !== partsCount) {
    t22 = isStorm && <div className="tw-inline-flex tw-relative tw-mt-2"><span className="tw-text-xs tw-text-iron-50 tw-mb-1.5">{currentPartIndex + 1} /{" "}<span className="tw-text-iron-400">{partsCount}</span></span></div>;
    $[48] = currentPartIndex;
    $[49] = isStorm;
    $[50] = partsCount;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== drop.author || $[53] !== isModalOpen) {
    t23 = <ArtistPreviewModal isOpen={isModalOpen} onClose={handleModalClose} user={drop.author} />;
    $[52] = drop.author;
    $[53] = isModalOpen;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== drop.author || $[56] !== isWinnerModalOpen) {
    t24 = <ArtistPreviewModal isOpen={isWinnerModalOpen} onClose={handleWinnerModalClose} user={drop.author} initialTab="winners" />;
    $[55] = drop.author;
    $[56] = isWinnerModalOpen;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== t19 || $[59] !== t21 || $[60] !== t22 || $[61] !== t23 || $[62] !== t24) {
    t25 = <>{t19}{t21}{t22}{t23}{t24}</>;
    $[58] = t19;
    $[59] = t21;
    $[60] = t22;
    $[61] = t23;
    $[62] = t24;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  return t25;
};
export default WaveDropHeader;